import { create } from 'zustand'
import { ViewWorker } from '@/lib/workers/viewWorker'
import { ServiceWorker } from '@/lib/workers/serviceWorker'
import { User } from '@/lib/api/client'
import { USER_GROUPS } from './constants'

interface CADPageState {
  viewWorker?: ViewWorker | null
  serviceWorker?: ServiceWorker | null
  viewUploadsInProgress: {
    viewId: string
    assemblyGroupId?: string | null
    documentOrder?: number | null
  }[]
  viewUploadsCompleted: number
  isViewUploaded: boolean
  isViewWorkerProcessing: boolean
  webworkerDebugger: {
    canvas: HTMLCanvasElement | null
    images: string[]
  }
  currentUser?: User | null
  onUserLogin: (user: User) => void
  onUserLogout: () => void
  initViewWorker: () => void
  initServiceWorker: () => void
  isUserReadOnly: () => boolean
  isUserSuperLooker: () => boolean
  setViewUploadsInProgress: (
    views: {
      viewId: string
      assemblyGroupId?: string | null
      documentOrder?: number | null
    }[],
  ) => void
  setViewUploadsCompleted: (viewIds: string[]) => void
  setViewWorkerHasKicked: () => void
  setViewWorkerHasStarted: () => void
  addWebworkerDebuggerImage: (image: string) => void
}

export const useAppStore = create<CADPageState>((set, get) => ({
  viewWorker: null,
  currentUser: null,
  viewUploadsInProgress: [],
  viewUploadsCompleted: 0,
  isViewUploaded: false,
  isViewWorkerProcessing: false,
  webworkerDebugger: {
    canvas: null,
    images: [],
  },
  onUserLogin: (user: User) => {
    set({
      currentUser: user,
    })
  },
  onUserLogout: () => {
    set({
      currentUser: null,
    })
  },

  addWebworkerDebuggerImage: (image: string) =>
    set((state) => {
      return {
        webworkerDebugger: {
          ...state.webworkerDebugger,
          images: [...state.webworkerDebugger.images, image],
        },
      }
    }),

  isUserReadOnly: () =>
    get().currentUser?.groups.includes(USER_GROUPS.READ_ONLY) || false,
  isUserSuperLooker: () =>
    get().currentUser?.groups.includes(USER_GROUPS.SUPER_LOOKER) || false,
  initViewWorker: () => {
    set((state) => {
      if (state.viewWorker) {
        return {}
      }
      const viewWorker = new ViewWorker({
        onUploadsInProgress: state.setViewUploadsInProgress,
        onUploadsCompleted: state.setViewUploadsCompleted,
        onScreenshot: state.addWebworkerDebuggerImage,
        onKick: state.setViewWorkerHasKicked,
        onStart: state.setViewWorkerHasStarted,
      })
      return {
        viewWorker,
        webworkerDebugger: { canvas: viewWorker.canvas, images: [] },
      }
    })
  },

  initServiceWorker: () => {
    set((state) => {
      if (state.serviceWorker) {
        return {}
      }
      const serviceWorker = new ServiceWorker()
      return {
        serviceWorker,
      }
    })
  },

  setViewWorkerHasKicked: () => set({ isViewWorkerProcessing: false }),

  setViewWorkerHasStarted: () => set({ isViewWorkerProcessing: true }),

  setViewUploadsInProgress: (
    views: {
      viewId: string
      assemblyGroupId?: string | null
      documentOrder?: number | null
    }[],
  ) => {
    set((state) => {
      const seenViews: {
        [k: string]: {
          viewId: string
          assemblyGroupId?: string | null
          documentOrder?: number | null
        }
      } = {}

      state.viewUploadsInProgress.forEach((view) => {
        seenViews[view.viewId] = view
      })

      views.forEach((view) => {
        seenViews[view.viewId] = view
      })

      const uniqueViews = Object.values(seenViews)

      return {
        viewUploadsInProgress: uniqueViews,
        isViewUploaded: false,
      }
    })
  },
  setViewUploadsCompleted: (viewIds: string[]) =>
    set((state) => {
      const viewUploadsInProgress = state.viewUploadsInProgress.filter(
        ({ viewId }) => !viewIds.includes(viewId),
      )
      return {
        isViewUploaded: true,
        viewUploadsInProgress,
        isViewWorkerProcessing: viewUploadsInProgress.length > 0,
        viewUploadsCompleted: state.viewUploadsCompleted + viewIds.length,
      }
    }),
}))
